import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import api from "@/lib/api";
import { toast } from "sonner";
import { History, RotateCcw, ChevronRight, Loader2, ArrowLeft } from "lucide-react";

// Simple word-level diff algorithm
function computeDiff(oldText, newText) {
  const oldWords = oldText.split(/(\s+)/);
  const newWords = newText.split(/(\s+)/);
  const m = oldWords.length, n = newWords.length;

  // Build LCS table (optimized for reasonable lengths)
  const maxLen = 2000;
  if (m > maxLen || n > maxLen) {
    // Fallback for very long texts: show as full replacement
    return [
      ...oldWords.map(w => ({ type: "removed", text: w })),
      ...newWords.map(w => ({ type: "added", text: w })),
    ];
  }

  const dp = Array.from({ length: m + 1 }, () => new Uint16Array(n + 1));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = oldWords[i - 1] === newWords[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  // Backtrack to build diff
  const result = [];
  let i = m, j = n;
  const stack = [];
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldWords[i - 1] === newWords[j - 1]) {
      stack.push({ type: "same", text: oldWords[i - 1] });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      stack.push({ type: "added", text: newWords[j - 1] });
      j--;
    } else {
      stack.push({ type: "removed", text: oldWords[i - 1] });
      i--;
    }
  }
  stack.reverse();
  return stack;
}

function stripHtml(html) {
  return (html || "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
}

function DiffView({ oldContent, newContent }) {
  const diff = useMemo(() => {
    return computeDiff(stripHtml(oldContent), stripHtml(newContent));
  }, [oldContent, newContent]);

  const stats = useMemo(() => {
    let added = 0, removed = 0;
    diff.forEach(d => {
      if (d.type === "added" && d.text.trim()) added++;
      if (d.type === "removed" && d.text.trim()) removed++;
    });
    return { added, removed };
  }, [diff]);

  return (
    <div data-testid="diff-view">
      <div className="flex items-center gap-3 mb-3 text-xs">
        <span className="text-emerald-600 font-medium">+{stats.added} ajouté{stats.added > 1 ? "s" : ""}</span>
        <span className="text-rose-600 font-medium">-{stats.removed} supprimé{stats.removed > 1 ? "s" : ""}</span>
      </div>
      <div className="text-sm leading-relaxed p-4 bg-muted/30 border rounded-none font-serif max-h-[400px] overflow-auto">
        {diff.map((part, i) => {
          if (part.type === "added") {
            return <span key={i} className="bg-emerald-100 text-emerald-900 px-0.5 rounded-sm">{part.text}</span>;
          }
          if (part.type === "removed") {
            return <span key={i} className="bg-rose-100 text-rose-900 line-through px-0.5 rounded-sm">{part.text}</span>;
          }
          return <span key={i}>{part.text}</span>;
        })}
      </div>
    </div>
  );
}

export default function VersionHistory({ articleId }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versionDetail, setVersionDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [currentArticle, setCurrentArticle] = useState(null);

  useEffect(() => {
    if (!articleId) return;
    loadVersions();
    loadCurrentArticle();
  }, [articleId]);

  const loadVersions = async () => {
    try {
      const res = await api.get(`/articles/${articleId}/versions`);
      setVersions(res.data.versions || []);
    } catch { /* no versions yet */ }
    setLoading(false);
  };

  const loadCurrentArticle = async () => {
    try {
      const res = await api.get(`/articles?admin=true&status=all&limit=200`);
      const art = (res.data.articles || []).find(a => a.id === articleId);
      if (art) setCurrentArticle(art);
    } catch { /* ignore */ }
  };

  const selectVersion = async (v) => {
    setSelectedVersion(v.id);
    setLoadingDetail(true);
    try {
      const res = await api.get(`/articles/${articleId}/versions/${v.id}`);
      setVersionDetail(res.data);
    } catch { toast.error("Erreur chargement version"); }
    setLoadingDetail(false);
  };

  const restoreVersion = async () => {
    if (!selectedVersion) return;
    if (!window.confirm("Restaurer cette version ? L'état actuel sera sauvegardé dans l'historique.")) return;
    setRestoring(true);
    try {
      await api.post(`/articles/${articleId}/versions/${selectedVersion}/restore`);
      toast.success("Version restaurée");
      window.location.reload();
    } catch { toast.error("Erreur restauration"); }
    setRestoring(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement de l&apos;historique...
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="text-center py-12" data-testid="no-versions">
        <History className="h-10 w-10 mx-auto mb-3 text-muted-foreground/40" />
        <p className="text-muted-foreground">Aucune version précédente</p>
        <p className="text-xs text-muted-foreground/60 mt-1">Les versions sont créées automatiquement a chaque sauvegarde</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[280px_1fr] gap-6" data-testid="version-history">
      {/* Version list */}
      <div className="border rounded-none">
        <div className="p-3 border-b bg-muted/30">
          <h3 className="font-medium text-sm flex items-center gap-2">
            <History className="h-4 w-4" /> {versions.length} version{versions.length > 1 ? "s" : ""}
          </h3>
        </div>
        <ScrollArea className="h-[450px]">
          {versions.map((v, i) => {
            const date = new Date(v.created_at);
            const isSelected = selectedVersion === v.id;
            return (
              <button
                key={v.id}
                onClick={() => selectVersion(v)}
                className={`w-full text-left p-3 border-b transition-colors hover:bg-muted/50 ${isSelected ? "bg-muted" : ""}`}
                data-testid={`version-item-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-mono text-muted-foreground">v{v.version_number}</span>
                  <Badge variant="outline" className="rounded-none text-[10px]">{v.status}</Badge>
                </div>
                <p className="text-sm font-medium mt-1 truncate">{v.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {date.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}{" "}
                  {date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </button>
            );
          })}
        </ScrollArea>
      </div>

      {/* Diff panel */}
      <div className="border rounded-none">
        {!versionDetail ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm py-12">
            <ChevronRight className="h-4 w-4 mr-2" /> Sélectionnez une version pour voir les changements
          </div>
        ) : loadingDetail ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin mr-2 text-muted-foreground" />
          </div>
        ) : (
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium">v{versionDetail.version_number}: {versionDetail.title}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {new Date(versionDetail.created_at).toLocaleString("fr-FR")}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="rounded-none"
                onClick={restoreVersion}
                disabled={restoring}
                data-testid="restore-version-btn"
              >
                {restoring ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RotateCcw className="h-3 w-3 mr-1" />}
                Restaurer cette version
              </Button>
            </div>

            {/* Title diff */}
            {currentArticle && versionDetail.title !== currentArticle.title && (
              <div className="mb-4">
                <p className="text-xs font-medium text-muted-foreground mb-1 uppercase tracking-wider">Titre</p>
                <DiffView oldContent={versionDetail.title} newContent={currentArticle.title} />
              </div>
            )}

            {/* Content diff */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1 uppercase tracking-wider">Contenu</p>
              {currentArticle ? (
                <DiffView oldContent={versionDetail.content} newContent={currentArticle.content} />
              ) : (
                <div className="text-sm p-4 bg-muted/30 border rounded-none max-h-[400px] overflow-auto">
                  <div dangerouslySetInnerHTML={{ __html: versionDetail.content }} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
